/**
 * Admin API client — Phase 27 persona CRUD + prompt editor + usage dashboard.
 *
 * All endpoints are gated server-side on the `admin` Cognito group; the
 * frontend AdminGuard also checks group membership before mounting any
 * page that calls these helpers.
 */

import { request } from "./api.js";

export const listAdminPersonas = () => request("/api/admin/personas");

export const getAdminPersona = (id) =>
  request(`/api/admin/personas/${encodeURIComponent(id)}`);

export const createAdminPersona = (payload) =>
  request("/api/admin/personas", {
    method: "POST",
    body: JSON.stringify(payload),
  });

export const updateAdminPersona = (id, payload) =>
  request(`/api/admin/personas/${encodeURIComponent(id)}`, {
    method: "PUT",
    body: JSON.stringify(payload),
  });

export const archiveAdminPersona = (id) =>
  request(`/api/admin/personas/${encodeURIComponent(id)}`, {
    method: "DELETE",
  });

export const setAdminPersonaEnabled = (id, enabled) =>
  request(`/api/admin/personas/${encodeURIComponent(id)}`, {
    method: "PATCH",
    body: JSON.stringify({ enabled }),
  });

// ─── Prompts ─────────────────────────────────────────────────────────────────

export const listAdminPrompts = () => request("/api/admin/prompts");

export const getAdminPrompt = (agentId) =>
  request(`/api/admin/prompts/${encodeURIComponent(agentId)}`);

export const updateAdminPrompt = (agentId, content) =>
  request(`/api/admin/prompts/${encodeURIComponent(agentId)}`, {
    method: "PUT",
    body: JSON.stringify({ content }),
  });

// ─── Usage ───────────────────────────────────────────────────────────────────

export const getAdminUsage = (channel) => {
  const qs = channel && channel !== "chat" ? `?channel=${encodeURIComponent(channel)}` : "";
  return request(`/api/admin/usage${qs}`);
};

// ─── Guardrails ──────────────────────────────────────────────────────────────

export const getAdminGuardrail = () => request("/api/admin/guardrails");

export const updateAdminGuardrail = (payload) =>
  request("/api/admin/guardrails", {
    method: "PUT",
    body: JSON.stringify(payload),
  });

// ─── Pipeline metrics (v5.1 Phase 52) ────────────────────────────────────────

export const PIPELINE_METRICS_DEFAULT_DAYS = 7;

/**
 * Build the ISO `since` cursor for the default time window. The backend
 * defaults to the same value when `since` is omitted, but the frontend
 * pins the cursor at mount time so trend re-fetches don't drift.
 */
export function buildDefaultSince(now = new Date()) {
  const t = new Date(
    now.getTime() - PIPELINE_METRICS_DEFAULT_DAYS * 86400 * 1000
  );
  return t.toISOString().replace(/\.\d{3}Z$/, "Z");
}


/**
 * Build the query string for the pipeline-metrics endpoints.
 *
 * `since` is an ISO-8601 timestamp; defaults server-side to 7d ago when
 * omitted. The optional `tenantId` is honoured only for super-admins;
 * tenant-admins are always forced to their own tenantId server-side.
 */
export function buildPipelineMetricsQuery({ since, tenantId, agentId, limit } = {}) {
  const params = new URLSearchParams();
  if (since) params.set("since", since);
  if (tenantId) params.set("tenantId", tenantId);
  if (agentId) params.set("agentId", agentId);
  if (limit) params.set("limit", String(limit));
  const qs = params.toString();
  return qs ? `?${qs}` : "";
}

export const getPipelineMetricsAgents = (opts = {}) =>
  request(`/api/admin/pipeline-metrics/agents${buildPipelineMetricsQuery(opts)}`);

export const getPipelineMetricsTrend = (opts = {}) =>
  request(`/api/admin/pipeline-metrics/trend${buildPipelineMetricsQuery(opts)}`);

export const getPipelineMetricsFailures = (opts = {}) =>
  request(`/api/admin/pipeline-metrics/failures${buildPipelineMetricsQuery(opts)}`);

// ─── Users (Cognito group management) ────────────────────────────────────────

export const listAdminUsers = () => request("/api/admin/users");

export const addAdminUser = (username) =>
  request(
    `/api/admin/users/${encodeURIComponent(username)}/admin`,
    { method: "POST" },
  );

export const removeAdminUser = (username) =>
  request(
    `/api/admin/users/${encodeURIComponent(username)}/admin`,
    { method: "DELETE" },
  );
